import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Layout from "../../../layouts";
import { EvidenceItem } from "../../../models/Evidence";
import { Trans, useTranslation } from "react-i18next";
import { common, input, report } from "../../../i18n";
import {
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import dayjs, { Dayjs } from "dayjs";

import { create } from "zustand";
import { useEffect, useMemo, useContext, useState } from "react";

import { OrganizationContext } from "../../../contexts/OrganizationContext";
import { CustomAutoComplete } from "../../../components/input/CustomAutoComplete";
import { useForm } from "react-hook-form";
import { FilterFileType } from "../../../components/input/UploadFileZone";
import Grid2 from "@mui/material/Unstable_Grid2";
import { MultipleAutoComplete } from "../../../components/input/MutipleAutoComplete";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import { CustomGridToolbarQuickFilter } from "../../../components/datagrid/CustomGridToolbarQuickFilter";
import { EvidenceService } from "../../../service/evidence.service";
import type { EvidenceDeleteResults } from "../../../models/dto/response/evidence/evidence-presigned-url-response.dto";
import { PaginationWithRowSelected } from "../../../components/datagrid/PaginationWithRowSelected";
import alertService from "../../../components/alert/alertService";
import { useAuth } from "../../../contexts/UserContext";
import { ApiError } from "../../../api/ApiError";

// Icon
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";
import ArrayUtils from "../../../utils/arrayUtils";

interface RenameForm {
  newFileName: string;
}

interface Filter {
  subOrganizations: string[];
  year: number;
  type: FilterFileType;
}

interface FilesManagementState {
  evidenceList: EvidenceItem[];
  setEvidenceItem: (evidenceList: EvidenceItem[]) => void;
}

export const filesManagementStore = create<FilesManagementState>((set) => ({
  evidenceList: [],
  setEvidenceItem: (evidenceList) => set({ evidenceList }),
}));

const FilesManagementContent = () => {
  const { evidenceList, setEvidenceItem } = filesManagementStore();

  const orgContext = useContext(OrganizationContext);
  const subOrganizationList = orgContext?.subOrganizations ?? [];

  const { isAdmin } = useAuth();

  const { t, i18n } = useTranslation([common, input, report], {
    nsMode: "fallback",
  });

  const { control, watch, getValues, setValue } = useForm<Filter>({
    defaultValues: {
      type: "all",
      subOrganizations: undefined,
      year: dayjs().year(),
    },
  });

  const renameForm = useForm<RenameForm>({
    reValidateMode: "onChange",
    defaultValues: {
      newFileName: "",
    },
  });

  // popper
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openPopper = Boolean(anchorEl);

  const [selectRow, setSelectRow] = useState<EvidenceItem>();

  const handleOnClickPopper = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: EvidenceItem,
  ) => {
    setSelectRow(row);
    setAnchorEl(event.currentTarget);
  };

  const onSubmit = async (data: RenameForm) => {
    if (selectRow) {
      let result;
      try {
        result = await EvidenceService.renameEvidences(
          selectRow.attachmentRef,
          data.newFileName,
        );
      } catch {
        // error alert already shown by service
        return;
      }

      if (result) {
        const changeItem = evidenceList.findIndex(
          (e) => e.attachmentRef === selectRow.attachmentRef,
        );

        evidenceList[changeItem].evidenceFileName = result;

        setEvidenceItem(evidenceList);

        alertService.mixin(t("edit-successfully"));
      }
    }
  };

  const renameEvidences = async () => {
    renameForm.reset();

    alertService.confirm(
      t("Rename-File"),
      <form onSubmit={renameForm.handleSubmit(onSubmit)}>
        <Box
          sx={{
            ".MuiFormHelperText-root": {
              color: "red !important",
            },
          }}
        >
          <InputReactHookForm
            name={"newFileName"}
            control={renameForm.control}
            label={t("File-Name")}
            placeholder={t("Name-File-Without-Extension")}
            required
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9_-]+$/,
                message: t("File-Name-Allowed-Characters"),
              },
            }}
          />
          <Button
            variant="contained"
            sx={{
              width: "100%",
              background: "var(--dark)",
              color: "var(--white)",
              [`&:hover`]: { background: "var(--dark)" },
              marginTop: "24px",
            }}
            type="submit"
          >
            {t("Save")}
          </Button>
        </Box>
      </form>,
      undefined,
      undefined,
      false,
    );
  };

  const deleteEvidences = async () => {
    const isConfirmed = await alertService.confirm(
      t("Delete"),
      <Trans
        t={t}
        i18nKey="Confirm-Delete-Multiple-Files"
        values={{ id: 1 }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
      t("Delete"),
    );

    if (isConfirmed && selectRow) {
      let result;
      try {
        result = await EvidenceService.deleteEvidences(
          selectRow.attachmentRef,
        );
      } catch {
        // error alert already shown by service
        return;
      }

      if (result) {
        setEvidenceItem(
          evidenceList.filter(
            (e) => e.attachmentRef !== selectRow.attachmentRef,
          ),
        );

        alertService.mixin(t("Delete-File-Success"));
      }
    }
  };

  const handleClose = async (event?: any) => {
    setAnchorEl(null);
    if (event.target.id === "rename") {
      renameEvidences();
    } else if (event.target.id === "deleted") {
      deleteEvidences();
    }
  };

  const apiRef = useGridApiRef();

  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 50,
    page: 0,
  });

  const subOrganizations = watch("subOrganizations");

  const year = watch("year");

  const type = watch("type");

  const openEvidence = async (attachmentRef: string): Promise<void> => {
    try {
      const file = await EvidenceService.getAttachmentByRef(attachmentRef);

      if (file) {
        window.open(file.evidenceS3Url);
      }
    } catch (error) {
      if (error instanceof ApiError) return;
      alertService.info(t("Cannot-Preview-File"));
    }
  };

  const mutipleDeleteActivities = async () => {
    const totalDeleteRow: number = selectionModel.length;

    const isConfirmed = await alertService.confirm(
      t("Multiple-Delete"),
      <Trans
        t={t}
        i18nKey="Confirm-Delete-Multiple-Files"
        values={{ id: totalDeleteRow }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
      t("Delete"),
    );

    if (isConfirmed) {
      let result: EvidenceDeleteResults | null = null;
      try {
        result = await EvidenceService.mutipleDeleteEvidences(
          selectionModel as string[],
        );
      } catch {
        // error alert already shown by service
        setSelectionModel([]);
        return;
      }

      if (result) {
        try {
          setEvidenceItem(
            evidenceList.filter(
              (e) =>
                !result!.evidence.success.attachmentRefs.includes(
                  e.attachmentRef,
                ),
            ),
          );
        } catch (error) {
          setEvidenceItem(evidenceList);
        }

        alertService.confirm(
          t("Multiple-Delete"),
          <Box>
            <TableContainer sx={{ width: "100%" }}>
              <Table
                sx={{
                  "& .MuiTableCell-root:first-child": {
                    paddingX: 0,
                    paddingY: "20px",
                  },
                  "& .MuiTableCell-root": {
                    paddingLeft: "40px",
                    paddingRight: "8px",
                    paddingY: "20px",
                    verticalAlign: "top",
                  },
                  "& .MuiTableCell-root:last-child": {
                    paddingY: "20px",
                    paddingX: 0,
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={2}>
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >{`${t("Number-Of-Delete-Records")}`}</Typography>
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >
                        {totalDeleteRow}
                      </Typography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >{`${t("Records")}`}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>

                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "none",
                      paddingTop: "20px !important",
                      paddingBottom: "0px !important",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        width: "24px",
                      }}
                    >
                      <CheckCircleIcon
                        color="success"
                        sx={{
                          fontSize: "24px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        sm: {
                          minWidth: "auto",
                        },
                        md: {
                          minWidth: "400px",
                        },
                        paddingLeft: "8px !important",
                        paddingRight: "0 !important",
                      }}
                    >
                      <Typography variant="text1">{`${t(
                        "Delete-Success",
                      )}`}</Typography>
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      {result.remark.deleteSuccess}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {t("Records")}
                    </TableCell>
                  </TableRow>

                  {result.evidence.failed.length > 0 &&
                    Object.keys(result!.remark.deleteFail).map(
                      (keys, index) =>
                        Object.values(result!.remark.deleteFail)[index] > 0 && (
                          <TableRow key={keys}>
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{
                                width: "24px",
                              }}
                            >
                              <ErrorIcon
                                color="error"
                                sx={{
                                  fontSize: "24px",
                                }}
                              />
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{
                                paddingLeft: "8px !important",
                                paddingRight: "0 !important",
                              }}
                            >
                              <Typography variant="text1">{`${t(
                                "Delete-Fail",
                              )}`}</Typography>
                              <br />
                              <Typography
                                variant="caption"
                                color={"var(--gray1)"}
                              >{`${t("Cannot-Delete-Recurring-Item", {
                                param: t(keys),
                              })}`}</Typography>
                            </TableCell>
                            <TableCell component="th" scope="row" align="right">
                              {Object.values(result!.remark.deleteFail)[index]}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              width={"auto"}
                            >
                              {t("Records")}
                            </TableCell>
                          </TableRow>
                        ),
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>,
          undefined,
          undefined,
          false,
          "auto",
        );
      }
    }

    setSelectionModel([]);
  };

  const getCurrentDisplayItem = () => {
    const firstIndex = paginationModel.page * paginationModel.pageSize;

    const lastIndex = (paginationModel.page + 1) * paginationModel.pageSize;

    const currentDisplayItem = [
      ...Array.from(apiRef.current.getRowModels().keys()).slice(
        firstIndex,
        lastIndex,
      ),
    ];

    return currentDisplayItem;
  };

  const checkedAll = () => {
    if (selectionModel.length === 0) return false;

    const currentDisplayItem = getCurrentDisplayItem();

    return currentDisplayItem.every((e) => selectionModel.includes(e));
  };

  const indeterminateAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();

    return (
      currentDisplayItem.some((e) => selectionModel.includes(e)) &&
      !checkedAll()
    );
  };

  const handleCheckboxAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();

    if (indeterminateAll()) {
      // ลบทั้งหมด
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
      ]);
    }

    if (currentDisplayItem.every((e) => selectionModel.includes(e))) {
      // กรณีที่รายการทั้งหมดซ้ำ เอาออกรายการที่ซ้ำออก
      return setSelectionModel(
        [...selectionModel, ...currentDisplayItem].filter(
          (e) => !currentDisplayItem.includes(e),
        ),
      );
    } else {
      // เพิ่มรายการทั้งหมด
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
        ...currentDisplayItem,
      ]);
    }
  };

  const handleCheckboxChange = (id: string) => {
    if (selectionModel.includes(id)) {
      setSelectionModel([...selectionModel, id].filter((e) => e !== id));
    } else {
      setSelectionModel([...selectionModel, id]);
    }
  };

  const columns: GridColDef<EvidenceItem>[] = [
    {
      field: "select",
      headerAlign: "center",
      renderHeader: (params) => {
        return (
          <Checkbox
            id={`checkbox-all`}
            checked={checkedAll()}
            indeterminate={indeterminateAll()}
            onChange={() => handleCheckboxAll()}
          />
        );
      },
      width: 70,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => (
        <Checkbox
          id={`checkbox-${params.row.attachmentRef}`}
          checked={selectionModel.includes(params.row.attachmentRef)}
          onChange={() => handleCheckboxChange(params.row.attachmentRef)}
        />
      ),
    },
    {
      field: "evidenceFileName",
      headerName: t("File-Name"),
      minWidth: 180,
      flex: 2,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.evidenceFileName}>
            <Box width={"100%"}>
              <Typography
                variant="text1"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => openEvidence(params.row.attachmentRef)}
              >
                {params.value}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "evidenceFileType",
      headerName: t("Type"),
      width: 180,
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
    },
    {
      field: "evidenceFileSize",
      headerName: t("Size"),
      width: 120,
      disableColumnMenu: true,
      type: "number",
      getApplyQuickFilterFn: undefined,
      renderCell: (params) => {
        const k = 1024;

        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

        const i = Math.floor(Math.log(params.value) / Math.log(k));

        const size = parseFloat((params.value / Math.pow(k, i)).toFixed(2));

        return `${size} ${sizes[i]}`;
      },
    },
    {
      field: "suborganizationRef",
      headerName: t("Sub-Organizations"),
      minWidth: 180,
      flex: 1,
      disableColumnMenu: true,
      valueFormatter: (value) =>
        subOrganizationList.find((s) => s.refId === value)?.name ?? "",
      getApplyQuickFilterFn: undefined,
    },

    {
      field: "updatedAt",
      headerName: t("Last-Update"),
      width: 180,
      disableColumnMenu: true,
      valueFormatter: (value) =>
        dayjs(value).locale(i18n.language).format("DD MMM YYYY"),
      getApplyQuickFilterFn: undefined,
    },

    {
      field: "createdAt",
      headerName: t("Upload-Date"),
      width: 180,
      disableColumnMenu: true,
      valueFormatter: (value) =>
        dayjs(value).locale(i18n.language).format("DD MMM YYYY"),
      getApplyQuickFilterFn: undefined,
    },
    {
      field: "",
      headerName: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 70,
      getApplyQuickFilterFn: undefined,
      getActions: (params) => [
        <GridActionsCellItem
          key={`${params.row.attachmentRef}-more`}
          icon={<img src="/img/more.svg" alt="icon-more" />}
          label="more"
          id="basic-button"
          aria-controls={openPopper ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openPopper ? "true" : undefined}
          onClick={(event) => handleOnClickPopper(event, params.row)}
        />,
      ],
    },
  ];

  const getEvidenceList = async () => {
    let result;
    try {
      result = await EvidenceService.getEvidencesAllSubOrg(year);
    } catch {
      // error alert already shown by service
      return;
    }

    setEvidenceItem(
      result.sort((a, b) => dayjs(b.updatedAt).diff(dayjs(a.updatedAt))) ?? [],
    );
  };

  useEffect(() => {
    getEvidenceList();
  }, [year]);

  useEffect(() => {
    if (subOrganizationList.length > 0) {
      setValue(
        "subOrganizations",
        subOrganizationList.map((r) => r.refId),
      );
    }
  }, [subOrganizationList, setValue]);

  const toolbarComponent = useMemo(() => {
    return () => (
      <Grid2
        container
        disableEqualOverflow
        marginBottom={"20px"}
        alignItems={"end"}
        spacing={"20px"}
      >
        <Grid2 xs={12} sm={4} marginBottom={1}>
          <MultipleAutoComplete
            name={"subOrganizations"}
            control={control}
            rules={undefined}
            label={t("Sub-Organizations")}
            info={""}
            placeholder={""}
            listMenuItem={ArrayUtils.convertArrayToMenuData(
              "refId",
              "name",
              "name",
              subOrganizationList,
            )}
            showLabelInput={false}
            defaultValue={[]}
            storeValue
          />
        </Grid2>

        <Grid2 xs={12} sm={"auto"} marginBottom={1}>
          <Box minWidth={"120px"}>
            <CustomDatePicker
              value={dayjs(`${year}-01-01`)}
              defaultDate={dayjs(`${year}-01-01`)}
              label={t("Year")}
              info={t("Year")}
              required
              handleOnChange={(date: Dayjs) => setValue("year", date.year())}
              views={["year"]}
              showInfo={false}
              format="YYYY"
            />
          </Box>
        </Grid2>

        <Grid2 xs={12} sm={"auto"}>
          <Box minWidth={"200px"}>
            <CustomAutoComplete
              name={"type"}
              control={control}
              rules={undefined}
              label={t("Type")}
              info={""}
              placeholder={""}
              showLabelInput={false}
              listMenuItem={[
                {
                  nameEN: t("All-Files"),
                  nameTH: t("All-Files"),
                  value: "all",
                },
                {
                  nameEN: t("IMAGE"),
                  nameTH: t("IMAGE"),
                  value: t("IMAGE"),
                },
                {
                  nameEN: t("DOCUMENT"),
                  nameTH: t("DOCUMENT"),
                  value: t("DOCUMENT"),
                },
              ]}
            />
          </Box>
        </Grid2>
        <Grid2 xs={6} sm={"auto"} marginBottom={1}>
          <CustomGridToolbarQuickFilter />
        </Grid2>

        {isAdmin() && (
          <Grid2 xs={6} sm={"auto"} marginBottom={1}>
            {selectionModel.length > 0 && (
              <IconButton onClick={() => mutipleDeleteActivities()}>
                <img src="/img/trash.svg" alt="trash" />
              </IconButton>
            )}
          </Grid2>
        )}
      </Grid2>
    );
  }, [
    control,
    subOrganizationList,
    year,
    setValue,
    t,
    selectionModel,
    mutipleDeleteActivities,
  ]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      height={"100%"}
      overflow={"auto"}
    >
      <Box
        flex={1}
        p={"40px 40px"}
        gap={"40px"}
        display={"flex"}
        flexDirection="column"
        alignItems={"start"}
        bgcolor={"var(--white)"}
        marginTop={1}
        minHeight={0}
      >
        <Box
          width={"100%"}
          height={"100%"}
          display={"flex"}
          flexDirection={"column"}
          gap={"40px"}
          color={"var(--dark)"}
          minHeight={0}
        >
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openPopper}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <MenuItem onClick={handleClose} id="rename">
              {t("Rename")}
            </MenuItem>

            <Divider />
            <MenuItem
              id="deleted"
              onClick={handleClose}
              disabled={!isAdmin()}
              sx={{
                color: "var(--red)",
              }}
            >
              {t("Delete")}
            </MenuItem>
          </Menu>
          <Box width={"100%"} height={"100%"} minHeight={0} flex={1}>
            <AppDataGrid
              apiRef={apiRef}
              rows={evidenceList
                .filter((e) => {
                  if (type === "all") {
                    return true;
                  } else {
                    return e.evidenceFileType === type;
                  }
                })
                .filter((e) => {
                  try {
                    return (
                      !!e.suborganizationRef &&
                      subOrganizations.includes(e.suborganizationRef)
                    );
                  } catch (error) {}

                  return false;
                })}
              columns={columns}
              rowHeight={60}
              slots={{
                toolbar: toolbarComponent,
                pagination: () => (
                  <PaginationWithRowSelected selectionModel={selectionModel} />
                ),
              }}
              localeText={{
                toolbarQuickFilterPlaceholder: `${t("Search-Files")}...`,
                noRowsLabel: `${t("No-Data")}`,
                footerRowSelected: (count) =>
                  t("Row-Selected", { param: count }),
              }}
              pagination
              sx={{
                ...tableStyle(),
                [`.selected`]: {
                  backgroundColor: "rgba(11, 148, 133, 0.08)",
                },
              }}
              getRowId={(row) => row.attachmentRef}
              pageSizeOptions={[25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              getRowClassName={(params: GridRowClassNameParams) => {
                return selectionModel.includes(params.id) ? "selected" : "";
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const FilesManagement = () => (
  <Layout>
    <FilesManagementContent />
  </Layout>
);
